"use client";

import React from "react";
import { Wrench, PhoneCall, FileCheck2, Clock, ShieldCheck, ArrowRight, Headphones, ChevronRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const supportHighlights = [{
  id: "h-1",
  title: "Breakdown & Field Repair",
  description: "Rapid engineer deployment across Ireland and the UK for mission-critical analytical instrumentation.",
  icon: Wrench,
  badge: "Priority Response"
}, {
  id: "h-2",
  title: "Qualification & Calibration",
  description: "Full IQ, OQ, PQ compliance protocols, preventive maintenance and certified recalibration.",
  icon: FileCheck2,
  badge: "GMP Ready"
}, {
  id: "h-3",
  title: "Application & Method Support",
  description: "Direct access to technical specialists for method optimization and troubleshooting.",
  icon: Headphones,
  badge: "Expert Advice"
}];
const fastStats = [{
  id: "s-1",
  label: "Dedicated Field Engineers",
  value: "Ireland & UK Wide"
}, {
  id: "s-2",
  label: "Response Target",
  value: "< 4 Hours"
}, {
  id: "s-3",
  label: "Laboratory Standards",
  value: "Quality-Focused Standards"
}];
export default function SupportHero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  return <section id="technicalsupporthero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Decorative background grid pattern */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="page-container relative z-10">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Hero Column */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Breadcrumb Navigation */}
            <motion.nav variants={fadeInVariants} aria-label="breadcrumb" className="mb-4">
              <ol className="flex items-center gap-1.5 text-xs text-muted-foreground sm:gap-2">
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="font-medium text-foreground" aria-current="page">
                    Support Request
                  </span>
                </li>
              </ol>
            </motion.nav>

            <motion.div variants={fadeInVariants} className="inline-flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3 py-1 font-medium border border-border">
                <ShieldCheck className="size-3.5 text-primary" />
                Technical Services & Engineering
              </Badge>
            </motion.div>

            <motion.h1 variants={fadeInVariants} className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default leading-tight mb-6">
              Laboratory Equipment & Analytical Technical Support
            </motion.h1>

            <motion.p variants={fadeInVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              Application-led analytical technology, qualification, and lifecycle support for regulated laboratories and manufacturing teams across Ireland and the UK.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={fadeInVariants} className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="shadow-sm font-medium">
                <Link to="/ContactPage">
                  Request Immediate Support
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="shadow-xs font-medium">
                <Link to="/ServicesPage">
                  View Service Contracts
                </Link>
              </Button>
            </motion.div>

            {/* Quick Metrics */}
            <motion.div variants={fadeInVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 mt-8 border-t border-border w-full">
              {fastStats.map((stat, i) => <div key={stat.id} data-index={i} className="flex flex-col">
                  <span className="text-base font-bold text-foreground font-default">
                    {stat.value}
                  </span>
                  <span className="text-xs text-muted-foreground mt-0.5">
                    {stat.label}
                  </span>
                </div>)}
            </motion.div>
          </div>

          {/* Right Cards Column */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="flex items-center justify-between px-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Priority Assistance Channels
              </span>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="size-3 text-primary" />
                <span>Standard Hours: 08:30 - 17:30</span>
              </div>
            </div>

            <div className="flex flex-col gap-3.5">
              {supportHighlights.map((item, i) => {
              const IconComponent = item.icon;
              return <motion.div key={item.id} variants={fadeInVariants} data-index={i}>
                    <Card className="border border-border/80 bg-card shadow-xs hover:border-primary/50 transition-colors">
                      <CardContent className="p-5 flex items-start gap-4">
                        <div className="rounded-lg bg-secondary p-2.5 text-primary shrink-0 border border-border">
                          <IconComponent className="size-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h2 className="text-sm font-semibold text-foreground truncate font-default">
                              {item.title}
                            </h2>
                            <span className="text-[10px] font-medium px-2 py-0.5 bg-muted text-muted-foreground rounded-md border border-border shrink-0">
                              {item.badge}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </div>

            {/* Direct Telephone Support Banner */}
            <motion.div variants={fadeInVariants} className="pt-2">
              <div className="rounded-xl bg-secondary/80 border border-border p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 text-primary p-2 shrink-0">
                    <PhoneCall className="size-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Direct Technical Helpdesk</p>
                    <p className="text-sm font-semibold text-foreground">Immediate Dispatch Available</p>
                  </div>
                </div>
                <Button asChild size="sm" variant="outline" className="shrink-0 bg-background">
                  <Link to="/ContactPage">
                    Contact Us
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}